import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './faqPage.css';
import Header from '../../components/common/header';
import Footer from '../../components/common/footer';
import '../home/homePage.css';
import { useUser } from '../../contexts/userContext';
import searchbarImage from '../../assets/images/search.svg';
import { useCart } from '../../contexts/cartContext';

interface FAQ {
  faq_id: number;
  question: string;
  answer: string;
}

interface FAQGroup {
  [key: string]: FAQ[];
}

interface FaqsOnlyProps {
  showContactButton?: boolean;
  showHeading?: boolean;
}

const FaqsOnly: React.FC<FaqsOnlyProps> = ({ showContactButton, showHeading }) => {
  const navigate = useNavigate();
  const [faqGroups, setFaqGroups] = useState<FAQGroup>({});
  const [expandedFaqId, setExpandedFaqId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const version = process.env.REACT_APP_VERSION;
  

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch(`${backendUrl}api/v2/All-Faqs`,
          {  method: 'GET',
            headers:{
              // Accept:'aplication/json',
              "Content-Type": "application/json",
      
            },
            credentials: "include",
            mode:"cors"          
          }
        );
        const data = await response.json();
        setFaqGroups(data.data); // Set grouped FAQs directly from API response
      } catch (error) {
        //console.error('Error fetching FAQs:', error);
        setError('Unable to fetch FAQs. Please try again later.');
      }
    };

    fetchFaqs();
    fetchSeoMetaDetails()
  }, [backendUrl]);

  const fetchSeoMetaDetails = async () => {
    try {
        const code = 'faq_page'
        const response = await fetch(`${backendUrl}${version}seo-meta-details/${code}`,
          {  method: 'GET',
            headers:{
              // Accept:'aplication/json',
              "Content-Type": "application/json",
      
            },
            credentials: "include",
            mode:"cors"      
          }
        );
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        await changeTitleAndMetaTags(data.result.page_title, {description:data.result.meta_description,keywords:data.result.meta_tag});
    } catch (error) {
        //console.error('Error fetching categories:', error);
    }
  };
  
  const changeTitleAndMetaTags = async (
    title: string,
    metaTags: { description?: string; keywords?: string; [key: string]: string | undefined }
  ) => {
    try {
        // Update the document title
        if (title) {
            document.title = title;
        } else {
            document.title = "Sell Used Appliances Online in India - Instant Cash & Free Pickup | Selsmart";
        }
  
        // Update or create meta tags
        Object.keys(metaTags).forEach((metaName) => {
            const metaTag = document.querySelector(`meta[name="${metaName}"]`);
            if (metaTag) {
                // Update existing meta tag
                metaTag.setAttribute('content', metaTags[metaName] || `Default ${metaName} for Selsmart`);
            } else {
                // Create new meta tag if it doesn't exist
                const newMetaTag = document.createElement('meta');
                newMetaTag.name = metaName;
                newMetaTag.content = metaTags[metaName] || `Default ${metaName} for Selsmart`;
                document.head.appendChild(newMetaTag);
            }
        });
  
        // Cleanup function to reset the title and meta tags when unmounting
        return () => {
            document.title = "Sell Used Appliances Online in India - Instant Cash & Free Pickup | Selsmart";
            Object.keys(metaTags).forEach((metaName) => {
                const metaTag = document.querySelector(`meta[name="${metaName}"]`);
                if (metaTag) {
                    metaTag.setAttribute('content', `Default ${metaName} for Selsmart`);
                }
            });
        };
    } catch (error) {
        //console.error("Error updating title and meta tags:", error);
    }
  };

  const handleFaqClick = (faqId: number) => {
    setExpandedFaqId(expandedFaqId === faqId ? null : faqId);
  };

  const handleContactUsClick = () => {
    navigate('/contact-us');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  // Filter FAQs across all groups based on the search query
  const filteredFaqGroups: FAQGroup = Object.keys(faqGroups).reduce((acc, groupName) => {
    const filteredFaqs = faqGroups[groupName].filter((faq) =>
      faq.question.toLowerCase().includes(searchQuery)
    );
    if (filteredFaqs.length > 0) {
      acc[groupName] = filteredFaqs;
    }
    return acc;
  }, {} as FAQGroup);

  return (
    <main className="main-content-faq">
      <div className="section_breadcrumb">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <a>FAQs</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <section className="faq">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="faq-content">
                {showHeading && <h1>FAQS</h1>}
                {error && <p className="error-message">{error}</p>}

                {/* Search Input */}
                <div className="faq-search">
                  <input
                    type="text"
                    placeholder="Search FAQs"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="search-inputs"
                  />
                  <img className="icon-search" src={searchbarImage} alt="Search" />
                </div>

                {Object.keys(filteredFaqGroups).length > 0 ? (
                  Object.entries(filteredFaqGroups).map(([groupName, faqs]) => (
                    <div key={groupName} className="faq-group">
                      <h2 className="faq-group-heading">{groupName}</h2>
                      {faqs.map((faq) => (
                        <div
                          key={faq.faq_id}
                          className={`faq-item ${expandedFaqId === faq.faq_id ? 'highlighted' : ''}`}
                        >
                          <div
                            className="faq-question"
                            onClick={() => handleFaqClick(faq.faq_id)}
                          >
                            <span>{faq.question}</span>
                            <button className="faq-toggle-button">
                              {expandedFaqId === faq.faq_id ? '-' : '+'}
                            </button>
                          </div>
                          {expandedFaqId === faq.faq_id && (
      <div className="faq-answer">
        {faq.answer ? (
          <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
        ) : (
          <p>{faq.answer}</p>
        )}
      </div>
    )}
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <p>No FAQs found for your search.</p>
                )}

                {showContactButton && (
                  <>
                    <p className="lookin-for">Couldn't find what you are looking for?</p>
                    <button onClick={handleContactUsClick} className="contact-button">
                      Contact us
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const FaqPage = () => {
  const navigate = useNavigate();
  const { checkSessionTokenExpirationStatus, handleRemoveLocalStorage ,fetchCsrfToken} = useUser();
  const { fetchCartQuantity,cartQuantity,setCartQuantity,handleQuantity } = useCart();
    const { setcityName, globalSearchTerm, setGlobalSearchTerm, setSessionCode, setUserId, setUsername, setUtmSessionId } = useUser();

    useEffect(() => {
      const isSessionHandled = sessionStorage.getItem("sessionHandled");
    
      // If the session was already handled, avoid running this logic again
      if (isSessionHandled) return;
    
      // console.log("------>>>>>>>>>>>>>>>>>aboutus-------", checkSessionTokenExpirationStatus());
    
      if (checkSessionTokenExpirationStatus()) {
        // Clear session-related operations
        handleQuantity();
        handleRemoveLocalStorage();
    
        // Set the sessionHandled flag to true to prevent re-execution after reload
        sessionStorage.setItem("sessionHandled", "true");
    
        // Redirect to the homepage
        navigate("/");
    
        // Reload the page to clear cache
        window.location.reload();
      }
    }, []);
  return (
    <div>
      <Header />
      <FaqsOnly showContactButton={true} showHeading={true} />
      <Footer />
    </div>
  );
};

export default FaqPage;
